import { BodyText } from '@/components/BodyText';
import { Ellipsis } from '@/components/Ellipsis';
import { FileUploadStatus, FileWithStatus } from '@/types/upload';
import { formatFileSize } from '@/utils/format';
import { ERROR_MESSAGES, isInvalidFileTypeError } from '@/utils/validation';
import {
  Box,
  IconButton,
  LinearProgress,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Theme,
  Tooltip,
  styled,
} from '@mui/material';
import { FolderOpenOutlined } from '@mui/icons-material';
import { Link as RouterLink } from 'react-router-dom';
import {
  FileStatusIcon,
  FileStatusText,
  SupportedFileTypesTooltip,
} from '@/components/Upload/components/UploadComponents';
import { isWarningUploadFailure } from '@/utils/getFileStatusText';
import { UploadTestIds } from '@/enums/uploadTestIds.enum';
import { useTenants } from '@/services/tenants/useTenants';

const SecondaryText = styled('span')(({ theme }) => ({
  color: theme.palette.text.secondary,
  fontSize: '0.875rem',
}));

const ErrorContainer = styled('span')(({ theme }) => ({
  display: 'flex',
  alignItems: 'flex-start',
  flexDirection: 'column',
  gap: theme.spacing(0.5),
}));

const FlexContainer = styled('span')(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(0.5),
}));

const getUploadProgress = (file: FileWithStatus, uploadProgress: Record<string, number>) => {
  if (file.status.state !== FileUploadStatus.UPLOADING) {
    return;
  }

  const progress = uploadProgress[file.path] || 0;
  const isDeterminate = progress > 0;

  return (
    <>
      <LinearProgress
        variant={'determinate'}
        value={progress}
        sx={{ width: '38px' }}
        data-testid={`${UploadTestIds.UploadRowProgressBar}-${file.path}`}
      />
      {isDeterminate && (
        <SecondaryText data-testid={`${UploadTestIds.UploadRowProgress}-${file.path}`}>{`${progress}%`}</SecondaryText>
      )}
    </>
  );
};

const getSecondaryContent = (file: FileWithStatus, uploadProgress: Record<string, number>) => {
  if (file.status.state === FileUploadStatus.FAILED) {
    return (
      <ErrorContainer>
        <span data-testid={`${UploadTestIds.UploadRowErrorMessage}-${file.path}`}>
          {isInvalidFileTypeError(file.status.message) ? (
            <SupportedFileTypesTooltip>
              <Box
                component='span'
                sx={{
                  color: 'error.main',
                  fontSize: '0.875rem',
                  cursor: 'text',
                  display: 'block',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                }}
              >
                {file.status.message}
              </Box>
            </SupportedFileTypesTooltip>
          ) : (
            <Ellipsis
              text={file.status.message ?? ERROR_MESSAGES.UPLOAD_FAILED}
              sx={{
                color: isWarningUploadFailure(file) ? 'warning.dark' : 'error.main',
                fontSize: '0.875rem',
              }}
            />
          )}
        </span>
        {file.status.traceId && (
          <span data-testid={`${UploadTestIds.UploadRowTraceId}-${file.path}`}>
            <Ellipsis
              text={`Error code: ${file.status.traceId}`}
              sx={{ color: 'text.secondary', fontSize: '0.7rem' }}
            />
          </span>
        )}
      </ErrorContainer>
    );
  }

  return (
    <FlexContainer>
      <SecondaryText>{formatFileSize(file.size)} </SecondaryText>
      {file.status.state === FileUploadStatus.UPLOADING ? (
        getUploadProgress(file, uploadProgress)
      ) : (
        <span data-testid={`${UploadTestIds.UploadRowStatusText}-${file.path}`}>
          <FileStatusText file={file} />
        </span>
      )}
    </FlexContainer>
  );
};

export const UploadFileListItem = ({
  file,
  uploadProgress,
  index,
}: {
  file: FileWithStatus;
  uploadProgress: Record<string, number>;
  index: number;
}) => {
  const { getPath } = useTenants();
  const assetHref = file.assetId ? getPath(`asset/${file.assetId}`) : undefined;

  const itemHref = file.folderId
    ? `assets?folderId=${file.folderId}&highlight=${file.assetId}`
    : `assets?highlight=${file.assetId}`;
  const folderHref = file.assetId ? getPath(itemHref) : undefined;

  const primaryContent = (
    <BodyText overflow='hidden'>
      <Ellipsis text={file.name} />
    </BodyText>
  );

  const rowContent = (
    <>
      <ListItemIcon sx={{ minWidth: '32px' }}>
        <FileStatusIcon file={file} />
      </ListItemIcon>
      <ListItemText primary={primaryContent} secondary={getSecondaryContent(file, uploadProgress)} />
    </>
  );

  const folderAction = folderHref && (
    <Tooltip title={file.folderId ? `Open folder containing ${file.name}` : 'Open root folder'}>
      <IconButton
        component={RouterLink}
        to={folderHref}
        size='small'
        aria-label={file.folderId ? `Open folder containing ${file.name}` : 'Open root folder'}
        data-testid={`${UploadTestIds.UploadRow}-${file.path}-folder-link`}
      >
        <FolderOpenOutlined fontSize='small' />
      </IconButton>
    </Tooltip>
  );

  const rowSx = { borderBottom: (theme: Theme) => `1px solid ${theme.palette.divider}` };
  const contentSx = { padding: (theme: Theme) => theme.spacing(1, 2) };

  if (assetHref) {
    return (
      <ListItem
        key={`${file.path}-${index}`}
        data-testid={`${UploadTestIds.UploadRow}-${file.path}`}
        disablePadding
        secondaryAction={folderAction}
        sx={rowSx}
      >
        <ListItemButton
          component={RouterLink}
          to={assetHref}
          data-testid={`${UploadTestIds.UploadRow}-${file.path}-asset-link`}
          sx={{ ...contentSx, color: 'inherit' }}
        >
          {rowContent}
        </ListItemButton>
      </ListItem>
    );
  }

  return (
    <ListItem
      key={`${file.path}-${index}`}
      data-testid={`${UploadTestIds.UploadRow}-${file.path}`}
      sx={{ ...contentSx, ...rowSx }}
    >
      {rowContent}
    </ListItem>
  );
};
